<template>
	<div class="reservation-view">

		<div class="table-container">
			<AppTable
				ref="reservations-table"
				:filters="[]"
				:table-headings="headings"
				:title="getString('warehouse_products')"
				:total-items="roomsOrdersStore.reservationsCurrDate.length"
				:items="currentShownReservations"
				@click-row="clickRow"
				@changed-page="v => currentPage = v"
				@changed-page-size="v => pageSize = v"
				@changed-sort="changeSort"
				@changed-filter="changeFilter" >

				<template #custom-header>
					<div class="header-container">
						<h3>{{ tableTitle }}</h3>
						<div class="search-container">
							<div class="actions-container">
								<AppLoader v-if="loadingData" :size="20" />
								<AppTextInput
									icon-name="icon-magnify"
									:placeholder="getString('search')"
									:value="roomsOrdersStore.reservationsSearch ? roomsOrdersStore.reservationsSearch : ''" @changed-text="changeSearch" />
								<div class="waitlist-btn" v-if="waitlistEnabled">
									<AppButtonIcon prevent-change icon-name="icon-hourglass" :title="getString('waiting_list')" @click-icon="roomsOrdersStore.waitlistSheetOpen = true" />
									<span class="count-badge" v-if="roomsOrdersStore.waitlistWaitingCount > 0">{{ roomsOrdersStore.waitlistWaitingCount }}</span>
								</div>
								<ReservationsViewToggle />
							</div>
						</div>
					</div>
				</template>
				<template #guest="{item}">
					<div class="guest-details">
						<div class="guest-name">{{ item.name }}</div>
						<div class="guest-phone">{{ item.phone ? item.phone : '' }}</div>
					</div>
				</template>
				<template #time="{item}">
					<div class="time-details">
						<div class="guest-details">
							<div class="guest-name">{{ item.timeDisplay }}</div>
							<div class="guest-phone">{{ item.dateDisplay }}</div>
						</div>
						<div class="bg-gradient" v-if="isLate(item)"></div>
						<div class="icon-availability" v-if="isLate(item)" v-html="svgStore.get('iconWarning')"></div>
					</div>
				</template>
				<template #status="{item}">
					<div class="status-container" :class="{ended: ['finished', 'noshow', 'canceled'].includes(item.status)}" v-if="!item.loadingUpdate">
						<div class="action-button" v-if="item.tablesRoom && item.tablesRoom.length === 0" @click.stop="editReservationTables(item)">
							<div class="action-color green"></div>
							<div class="action-text">{{ getString('assign_table') }}</div>
						</div>
						<div class="action-button" v-if="isArrivedIf(item)" @click.stop="updateStatus(item, 'arrived')">
							<div class="action-color yellow"></div>
							<div class="action-text">{{ getString('arrived') }}</div>
						</div>
						<div class="action-button" v-if="isNoShowIf(item)" :class="{done: item.status === 'noshow'}" @click.stop="confirmPopup = 'noshow';reservationPopup = item;$refs['res-confirm-popup'].toggleStatus(true)">
							<div class="action-color orange"></div>
							<div class="action-text">{{ getString('no_show') }}</div>
						</div>
						<div class="action-button" v-if="isCanceledIf(item)" :class="{done: item.status === 'canceled'}" @click.stop="confirmPopup = 'canceled';reservationPopup = item;$refs['res-confirm-popup'].toggleStatus(true)">
							<div class="action-color danger"></div>
							<div class="action-text">{{ getString('cancel') }}</div>
						</div>
						<div class="action-button" v-if="isFinishedIf(item)" :class="{done: item.status === 'finished'}" @click.stop="updateStatus(item, 'finished')">
							<div class="action-color blue"></div>
							<div class="action-text">{{ getString('finished') }}</div>
						</div>
					</div>
					<div class="status-loading" v-else>
						<AppLoader :size="20" />
					</div>
				</template>
				<template #tables="{item}">
					<div class="tables-container" v-if="item.tablesRoom && item.tablesRoom.length" :key="item.reservation_id" @click.stop="editReservationTables(item)">
						<div class="double-info tables-row" v-for="table in item.tablesRoom">
							<div class="first-info">
								<p class="info-value first">
									<span class="value-content">{{ table.table_name }}</span>
									<span class="value-icon" v-html="svgStore.get('iconAnalyticsTable')"></span>
								</p>
							</div>
							<div class="second-info">
								<p class="info-value">
									<span class="value-content">{{ table.room_name }}</span>
								</p>
							</div>
						</div>
					</div>
					<div class="no-tables" @click.stop="editReservationTables(item)" v-else><p>{{ getString('no_tables') }}</p></div>
				</template>
				<template #experience="{item}">
					<p>{{ getExperienceName(item) }}</p>
				</template>
			</AppTable>
		</div>

		<AppPopup
			:title="getString('are_you_sure')"
			:body-text="popupStatusBody"
			:buttons="popupStatusButtons"
			ref="res-confirm-popup"
		/>
	</div>
</template>

<script>
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppTable from "@/shared/components/AppTable.vue";
import {useUserStore} from "@/user/stores";
import {useRoomsOrdersStore, useExperiencesStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ReservationsViewToggle from "@/console-new/components/ops/tables/ReservationsViewToggle.vue";
import API from "@/shared/helpers/API";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const experiencesStore = useExperiencesStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		const notificationStore = useNotificationStore();
		return {
			getString: languageStore.getString,
			languageStore,
			roomsOrdersStore,
			experiencesStore,
			notificationStore,
			svgStore,
			userStore
		};
	},
	data() {
		return {
			confirmPopup: null,
			reservationPopup: null,
			pageSize: 10,
			currentPage: 1,
			sortField: "timeDisplay",
			sortAsc: true,
			loadingUpdate: false,
			loadingData: false,
			headings: [
				{
					text: this.getString('time'),
					fieldName: 'timeDisplay',
					sortable: true,
					cols: 1,
					slotName: 'time',
				},
				{
					text: this.getString('guest'),
					fieldName: 'name',
					cols: 2,
					slotName: 'guest',
				},
				{
					text: this.getString('people'),
					fieldName: 'people',
					cols: 1,
					sortable: true,
				},
				{
					text: this.getString('status'),
					fieldName: 'name',
					cols: 2,
					slotName: 'status',
				},
				{
					text: this.getString('tables'),
					fieldName: 'name',
					cols: 3,
					slotName: 'tables',
				},
				{
					text: this.getString('experience'),
					fieldName: 'experience_id',
					cols: 2,
					slotName: 'experience',
				},
			],
		}
	},
	name: "ReservationView",
	components: {AppTextInput, ReservationsViewToggle, AppLoader, AppPopup, AppTable, AppButton, AppButtonIcon},
	computed: {
		waitlistEnabled() {
			return !!(this.userStore.currBusiness && Number(this.userStore.currBusiness.reservations_waitlist));
		},
		popupStatusButtons() {
			if(this.confirmPopup === "noshow") {
				return [
					{text: this.getString('cancel'), callback: null, negative: true},
					{text: this.getString('confirm'), callback: () => {
						this.updateStatus(this.reservationPopup, 'noshow');
						this.$refs['res-confirm-popup'].toggleStatus(false);
					}},
				]
			} else if(this.confirmPopup === "canceled") {
				const withPayment = this.reservationPopup.payment_mode === "payment" && this.reservationPopup.amount_charged > 0;
				if(withPayment) {
					return [
						{text: this.getString('cancel'), callback: null, negative: true},
						{text: this.getString('no_refund'), negative: true, callback: () => {
							this.updateStatus(this.reservationPopup, 'canceled');
							this.$refs['res-confirm-popup'].toggleStatus(false);
						}},
						{text: this.getString('refund'), callback: () => {
							this.updateStatus(this.reservationPopup, 'canceled', true);
							this.$refs['res-confirm-popup'].toggleStatus(false);
						}},
					]
				} else {
					return [
						{text: this.getString('cancel'), callback: null, negative: true},
						{text: this.getString('confirm'), callback: () => {
							this.updateStatus(this.reservationPopup, 'canceled');
							this.$refs['res-confirm-popup'].toggleStatus(false);
						}},
					]
				}
			} else {
				return [];
			}
		},
		popupStatusBody() {
			if(!this.reservationPopup) {
				return '';
			}
			if(this.confirmPopup === "noshow") {
				const withPayment = this.reservationPopup.payment_mode !== null;
				return this.getString('confirm_no_show')+(withPayment ? " "+this.getString('confirm_no_show_payment') : '');
			} else if(this.confirmPopup === "canceled") {
				const withPayment = this.reservationPopup.payment_mode === "payment" && this.reservationPopup.amount_charged > 0;
				return this.getString('confirm_canceled')+(withPayment ? " "+this.getString('confirm_canceled_payment') : '');
			} else {
				return '';
			}
		},
		tableTitle() {
			const resNum = this.roomsOrdersStore.reservationsCurrDate.length;
			const peopleNum = this.roomsOrdersStore.reservationsCurrDate.reduce((acc, curr) => acc + Number(curr.people), 0);

			let reservationsText = this.getString('reservations_x', [resNum]);
			if (resNum === 1) {
				reservationsText = this.getString('one_reservation');
			}
			let peopleText = this.getString('people_x', [peopleNum]);
			if (peopleNum === 1) {
				peopleText = this.getString('one_person');
			}

			return `${reservationsText} - ${peopleText}`;
		},
		currentShownReservations() {
			return this.filteredReservations.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
		},
		filteredReservations() {
			let reservations = this.roomsOrdersStore.reservationsCurrDate;

			if(this.sortField) {
				if(this.sortField === "timeDisplay") {
					reservations.sort((a, b) => {
						let comp = a["fullDate"] - b["fullDate"];
						if(!this.sortAsc) {
							comp = -comp;
						}
						return comp;
					});
				} else {
					reservations.sort((a, b) => {
						let comp = a[this.sortField] - b[this.sortField];
						if(!this.sortAsc) {
							comp = -comp;
						}
						return comp;
					});
				}
			}

			return this.filterReservations(reservations);
		},
	},
	methods: {
		changeSearch(v) {
			this.roomsOrdersStore.reservationsSearch = v.length > 0 ? v : null;

			this.currentPage = 1;
			this.$refs['reservations-table'].$refs['pagination-btns'].activePage = 1;
			this.loadingData = true;
			this.roomsOrdersStore.getDayReservations(() => {
				this.loadingData = false;
			});
		},
		updateStatus(item, status, refund = false) {
			item.loadingUpdate = true;
			this.roomsOrdersStore.updateReservationStatus(item.reservation_id, status, refund, (r) => {
				if(r && r.result && r.result === "OK") {
					item.status = status;
					this.roomsOrdersStore.getDayReservations(() => {
						item.loadingUpdate = false;
					});
				} else {
					this.notificationStore.showNotification("generic-error");
					item.loadingUpdate = false;
				}
			});
		},
		clickRow(item) {
			this.roomsOrdersStore.manageReservationId = item.reservation_id;
		},
		editReservationTables(item) {
			this.roomsOrdersStore.editReservationId = item.reservation_id;
			setTimeout(() => {
				this.roomsOrdersStore.editReservationTables = true;
			}, 200);
		},
		isArrivedIf(reservation) {
			return reservation.status === "confirmed" && (reservation.fullDate - new Date()) < 86400 * 1000;
		},
		isNoShowIf(reservation) {
			return ["confirmed", "noshow"].includes(reservation.status) && (reservation.fullDate - new Date()) < 86400 * 1000;
		},
		isCanceledIf(reservation) {
			return ["confirmed", "canceled"].includes(reservation.status);
		},
		isFinishedIf(reservation) {
				return ["arrived", "finished"].includes(reservation.status);
		},
		getExperienceName(item) {
				if(!item || !item.experience_id) { return ''; }
				const exp = this.experiencesStore.getExperienceById(Number(item.experience_id));
				return exp ? exp.name : '';
		},
		filterReservations(reservations) {
			if(!reservations || !reservations.length) {
					return reservations;
			}
			return reservations;
		},
		changeSort(newSortField, newSortAsc) {
			this.currentPage = 1;
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			this.$refs['reservations-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilter(newFilters) {
			this.currentPage = 1;
			this.$refs['reservations-table'].$refs['pagination-btns'].activePage = 1;
		},
		isLate(item, test = false) {
			return item?.status && !['arrived', 'finished', 'noshow', 'canceled'].includes(item.status) && item.fullDate < new Date()
		},
	},
	mounted() {
		this.loadingData = true;
		if(this.userStore.currBusinessId !== null) {
			this.roomsOrdersStore.getDayReservations(() => {
				this.loadingData = false;
			});
		}

		if(this.userStore.hasReservations && !this.experiencesStore.businessExperiences) {
			this.experiencesStore.getBusinessExperiences();
		}

	},
	watch: {
		"roomsOrdersStore.reservationsSelectedDate": function (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.loadingData = true;
				this.roomsOrdersStore.getDayReservations(() => {
					this.loadingData = false;
				});
			}
		},
		"roomsOrdersStore.reservationsDayToPrint": function (newVal, oldVal) {
			if (newVal && newVal !== oldVal) {
				if (this.currentShownReservations.length === 0) {
					this.notificationStore.showNotification('no-reservation-to-print');
					this.roomsOrdersStore.reservationsDayToPrint = null;
					return;
				}

				let filters = {};

				if (Object.entries(this.roomsOrdersStore.reservationsFilters).length > 0) {
					filters = this.roomsOrdersStore.reservationsFilters;
				}
				else {
					filters = {
						status: this.roomsOrdersStore.standardStatus,
						experiences: this.experiencesStore.hasExperiences ? this.experiencesStore.businessExperiences.map((e) => e.experience_id) : []
					}
				}

				API.init().printReservationsDay(this.userStore.currBusinessId, newVal, filters, (response) => {
					if (response.result === 'OK') {
						this.notificationStore.showNotification('print-reservations-success');
					} else {
						this.notificationStore.showNotification('generic-error');
					}
					this.roomsOrdersStore.reservationsDayToPrint = null;
				}, (error) => {
					this.notificationStore.showNotification('generic-error');
					this.roomsOrdersStore.reservationsDayToPrint = null;
				});
			}
		},
		"userStore.currBusinessId": function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				// Business context is now ready, load reservations
				this.loadingData = true;
				this.roomsOrdersStore.getDayReservations(() => {
					this.loadingData = false;
				});

				if(this.userStore.hasReservations && !this.experiencesStore.businessExperiences) {
					this.experiencesStore.getBusinessExperiences();
				}
			}
		},
	}
}
</script>

<style scoped lang="scss">
.reservation-view {
	padding: var(--orders-padding);
	display: flex;
	justify-content: center;

	.table-container {
		display: flex;
		justify-content: center;
		max-width: 1620px;
		width: 100%;

		.time-details {
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 100%;
			gap: 8px;

			.icon-availability {
				width: 16px;
				height: 16px;
				display: flex;
				align-items: center;
				justify-content: center;

				:deep(svg) {
					width: 16px;
					height: 16px;

					.fill {
						fill: var(--color-danger);
					}

					.stroke {
						stroke: var(--color-danger);
					}
				}
			}
			.bg-gradient {
				position: absolute;
				left: 0;
				top: 0;
				width: 100%;
				height: 100%;
				background: linear-gradient(270deg,rgba(252, 34, 145, 0) 0%, rgba(252, 34, 145, 15%) 100%);
				z-index: 1;
				pointer-events: none;
			}
		}

		.guest-details {
			display: flex;
			flex-direction: column;
			gap: 2px;
			z-index: 3;
			.guest-name {
				font-size: 14px;
				color: var(--color-txt);
			}
			.guest-phone {
				font-size: 14px;
				color: var(--color-txt-2);
			}
		}

		.status-container {
			display: flex;
			justify-content: center;
			align-items: flex-start;
			flex-direction: column;
			border: 1px solid var(--color-primary);
			cursor: pointer;
			border-radius: 13px;
			width: 100%;

			.action-button {
				display: flex;
				justify-content: flex-start;
				align-items: center;
				gap: 5px;
				border-bottom: 1px solid var(--color-primary);
				width: 100%;
				padding: 4px 12px;


				&:first-child {
					border-radius: 13px 13px 0 0;
				}

				&:last-child {
					border-radius: 0 0 13px 13px;
					border-bottom: none;
				}

				&.done {

					.action-text {
						color: var(--color-txt-2);
					}
					.action-color {
						background-color: var(--color-txt-2);
					}
				}
			}

			&.ended {
				border: 1px solid var(--color-txt-2);
			}

			.action-text {
				font-size: 14px;
				color: var(--color-text-primary);
			}

			.action-color {
				width: 10px;
				height: 10px;
				border-radius: 50%;

				&.blue {
					background-color: var(--color-primary);
				}

				&.green {
					background-color: var(--color-secondary);
				}

				&.yellow {
					background-color: #FDC700;
				}

				&.orange {
					background-color: #FC7502;
				}

				&.danger {
					background-color: var(--color-danger);
				}

				&.grey {
					background-color: var(--color-txt-2);
				}
			}

		}

		.tables-container {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-start;
			max-height: 115px;
			overflow: auto;
			align-items: flex-start;
			width: 100%;
			height: 100%;
			gap: 4px;
			cursor: pointer;
		}

		.double-info {
			display: flex;
			justify-content: space-between;
			gap: 8px;

			.info-label {
				font-size: 12px;
				color: var(--color-txt-1);
			}

			.info-value {
				display: flex;
				align-items: center;
				justify-content: space-between;
				font-size: 16px;
				color: var(--color-text-primary);
				background-color: var(--color-bg-primary-1);
				border-radius: 13px;
				padding: 6px 10px;
				min-height: 35px;
				gap: 4px;
				min-width: 90px;

				&.first {
					min-width: 120px;
				}

				.value-content {
					margin-right: 4px;
					font-size: 14px;
				}

				.value-icon {
					width: 16px;
					height: 16px;
					display: flex;
					align-items: center;
					justify-content: center;

					:deep(svg) {
						width: 16px;
						height: 16px;

						.fill {
							fill: var(--color-primary);
						}

						.stroke {
							stroke: var(--color-primary);
						}

					}
				}
			}
		}

		:deep(.table-row-body) {
			cursor:  pointer;
		}
	}

	.no-tables {
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: flex-start;
		align-items: center;

		p {
			font-size: 14px;
		}
	}

	.actions-container {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 8px;

		:deep(.input-wrapper) {
			width: 240px;
		}
	}

	.waitlist-btn {
		position: relative;

		.count-badge {
			position: absolute;
			top: -6px;
			right: -6px;
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: 18px;
			height: 18px;
			padding: 0 5px;
			border-radius: 9px;
			background-color: var(--color-primary);
			color: #fff;
			font-size: 12px;
		}
	}

	.header-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		width: 100%;
		gap: 12px;

		.search-container {
			margin-left: auto;

			@media (max-width: 480px) {
				width: 100%;

				.input-wrapper {
					width: 100%;
				}
			}
		}
	}
}
</style>